import React, { useState, useEffect, useCallback } from 'react';
import {
    Page,
    Layout,
    Card,
    BlockStack,
    InlineStack,
    Text,
    Button,
    Spinner,
    Banner,
    Badge,
    DataTable,
    Modal,
    FormLayout,
    TextField,
    Select
} from '@shopify/polaris';
import { commissionService } from '../services/commissionService';
import { accountingHelpers, COMMISSION_STATUSES } from '../constants/accountingConstants';
import KPICard from '../components/accounting/KPICard';

const CommissionsPage = () => {
    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [report, setReport] = useState(null);
    const [agents, setAgents] = useState([]);

    // Period selector
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    // Modal state
    const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [reportRes, agentsRes] = await Promise.all([
                commissionService.getMonthlyReport(year, month),
                commissionService.getSalesAgents()
            ]);

            setReport(reportRes.data || null);
            setAgents(agentsRes.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handle calculate all
    const handleCalculateAll = async () => {
        setSubmitting(true);
        try {
            const startDate = new Date(year, month - 1, 1).toISOString();
            const endDate = new Date(year, month, 0).toISOString();

            await commissionService.calculatePeriodCommissions(startDate, endDate);
            setSuccessMessage('Commissions calculated successfully');
            loadData();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle mark paid
    const handleMarkPaid = async () => {
        if (!selectedAgent) return;

        setSubmitting(true);
        try {
            await commissionService.markCommissionsAsPaid(selectedAgent.user_id, year, month);
            setSuccessMessage(`Commissions marked as paid for ${selectedAgent.name}`);
            setShowMarkPaidModal(false);
            setSelectedAgent(null);
            loadData();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusConfig = COMMISSION_STATUSES[status?.toUpperCase()] || COMMISSION_STATUSES.PENDING;
        return <Badge tone={statusConfig.badge}>{statusConfig.label}</Badge>;
    };

    // Render agents table
    const renderAgentsTable = () => {
        const agentsList = report?.agents || [];

        if (agentsList.length === 0) {
            return <Text as="p" tone="subdued">No sales agents with commissions this period</Text>;
        }

        const rows = agentsList.map(agent => [
            agent.name || 'Unknown',
            agent.total_orders,
            accountingHelpers.formatCurrency(agent.total_sales),
            `${agent.commission_rate || 0}%`,
            accountingHelpers.formatCurrency(agent.total_commission),
            getStatusBadge(agent.status || 'pending'),
            <InlineStack gap="200">
                <Button
                    size="slim"
                    onClick={() => {
                        setSelectedAgent(agent);
                        setShowMarkPaidModal(true);
                    }}
                    disabled={agent.status === 'paid'}
                >
                    Mark Paid
                </Button>
            </InlineStack>
        ]);

        return (
            <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'text', 'numeric', 'text', 'text']}
                headings={['Agent', 'Orders', 'Sales', 'Rate', 'Commission', 'Status', 'Actions']}
                rows={rows}
            />
        );
    };

    // Generate year options
    const yearOptions = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) {
        yearOptions.push({ label: String(y), value: String(y) });
    }

    // Generate month options
    const monthOptions = [
        { label: 'January', value: '1' },
        { label: 'February', value: '2' },
        { label: 'March', value: '3' },
        { label: 'April', value: '4' },
        { label: 'May', value: '5' },
        { label: 'June', value: '6' },
        { label: 'July', value: '7' },
        { label: 'August', value: '8' },
        { label: 'September', value: '9' },
        { label: 'October', value: '10' },
        { label: 'November', value: '11' },
        { label: 'December', value: '12' }
    ];

    if (loading) {
        return (
            <Page title="Sales Commissions">
                <Layout>
                    <Layout.Section>
                        <Card>
                            <InlineStack align="center">
                                <Spinner size="large" />
                            </InlineStack>
                        </Card>
                    </Layout.Section>
                </Layout>
            </Page>
        );
    }

    const summary = report?.summary || {};

    return (
        <Page
            title="Sales Commissions"
            subtitle="Track sales agent performance and manage commission payments"
            primaryAction={{
                content: 'Calculate All',
                onAction: handleCalculateAll,
                loading: submitting
            }}
        >
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner tone="critical" onDismiss={() => setError(null)}>
                            {error}
                        </Banner>
                    </Layout.Section>
                )}

                {successMessage && (
                    <Layout.Section>
                        <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Banner>
                    </Layout.Section>
                )}

                {/* Period Selector */}
                <Layout.Section>
                    <Card>
                        <InlineStack gap="400" blockAlign="end">
                            <Select
                                label="Year"
                                options={yearOptions}
                                value={String(year)}
                                onChange={(value) => setYear(parseInt(value))}
                            />
                            <Select
                                label="Month"
                                options={monthOptions}
                                value={String(month)}
                                onChange={(value) => setMonth(parseInt(value))}
                            />
                            <Button onClick={loadData}>Load</Button>
                        </InlineStack>
                    </Card>
                </Layout.Section>

                {/* Summary Cards */}
                <Layout.Section>
                    <BlockStack gap="400">
                        <Text as="h2" variant="headingMd">Commission Summary - {summary.period || ''}</Text>
                        <InlineStack gap="400">
                            <KPICard
                                title="Total Agents"
                                value={summary.total_agents || 0}
                                format="number"
                            />
                            <KPICard
                                title="Total Orders"
                                value={summary.total_orders || 0}
                                format="number"
                            />
                            <KPICard
                                title="Total Sales"
                                value={summary.total_sales || 0}
                                format="currency"
                            />
                            <KPICard
                                title="Total Commission"
                                value={summary.total_commission || 0}
                                format="currency"
                            />
                        </InlineStack>
                        <InlineStack gap="400">
                            <KPICard
                                title="Pending Commission"
                                value={summary.pending_commission || 0}
                                format="currency"
                                subtitle="Awaiting payment"
                            />
                            <KPICard
                                title="Paid Commission"
                                value={summary.paid_commission || 0}
                                format="currency"
                                subtitle="Already paid out"
                            />
                        </InlineStack>
                    </BlockStack>
                </Layout.Section>

                {/* Agents Table */}
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">Sales Agents Performance</Text>
                            {renderAgentsTable()}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>

            {/* Mark Paid Modal */}
            <Modal
                open={showMarkPaidModal}
                onClose={() => {
                    setShowMarkPaidModal(false);
                    setSelectedAgent(null);
                }}
                title="Mark Commission as Paid"
                primaryAction={{
                    content: 'Confirm Payment',
                    onAction: handleMarkPaid,
                    loading: submitting
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => {
                        setShowMarkPaidModal(false);
                        setSelectedAgent(null);
                    }
                }]}
            >
                <Modal.Section>
                    {selectedAgent && (
                        <BlockStack gap="300">
                            <Text as="p">
                                Are you sure you want to mark the commission as paid for <strong>{selectedAgent.name}</strong>?
                            </Text>
                            <Card>
                                <BlockStack gap="200">
                                    <InlineStack align="space-between">
                                        <Text as="p">Period:</Text>
                                        <Text as="p" fontWeight="bold">{monthOptions[month - 1]?.label} {year}</Text>
                                    </InlineStack>
                                    <InlineStack align="space-between">
                                        <Text as="p">Orders:</Text>
                                        <Text as="p" fontWeight="bold">{selectedAgent.total_orders}</Text>
                                    </InlineStack>
                                    <InlineStack align="space-between">
                                        <Text as="p">Commission Amount:</Text>
                                        <Text as="p" fontWeight="bold" tone="success">
                                            {accountingHelpers.formatCurrency(selectedAgent.total_commission)}
                                        </Text>
                                    </InlineStack>
                                </BlockStack>
                            </Card>
                        </BlockStack>
                    )}
                </Modal.Section>
            </Modal>
        </Page>
    );
};

export default CommissionsPage;
